/**
 * RitualistAvatar Component
 * 300x300 card with full-bleed cover image, bottom gradient overlay,
 * and hover/focus expanded state with bio, PROFILE/FOLLOW buttons.
 */

import { MoreVertical } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { HeartFilledIcon, HeartOutlineIcon } from "@/components/icons"
import { ReportUserModal } from "@/components/shared/ReportUserModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"
import type { HighlightedExpert, HighlightedRitualist } from "../types/connect.types"

interface RitualistAvatarProps {
  ritualist: HighlightedRitualist | HighlightedExpert
  onFollow?: (id: string) => void
  onUnfollow?: (id: string) => void
}

export function RitualistAvatar({ ritualist, onFollow, onUnfollow }: RitualistAvatarProps) {
  const navigate = useNavigate()
  const isGuest = !useAuthStore(state => state.user)
  const { t } = useTranslation("common")
  const [isExpanded, setIsExpanded] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  const showCard = () => setIsExpanded(true)
  const hideCard = () => {
    if (!isDropdownOpen) {
      setIsExpanded(false)
    }
  }

  return (
    <div
      className="relative w-[300px] h-[300px] rounded-[20px] overflow-hidden flex-shrink-0 cursor-pointer"
      onMouseEnter={showCard}
      onMouseLeave={hideCard}
      onFocus={showCard}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          hideCard()
        }
      }}
    >
      {/* Full-bleed cover image */}
      {ritualist.profilePictureUrl ? (
        <img
          src={ritualist.profilePictureUrl}
          alt={ritualist.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img
          src={getAssetUrl("upload-bg.jpeg")}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Default state: bottom gradient with name, handle, ritual count */}
      {!isExpanded && (
        <div className="absolute inset-x-0 bottom-0 h-[70px] overflow-hidden">
          {/* Layer 1 */}
          <div
            className="absolute inset-0 backdrop-blur-[4px]"
            style={{
              maskImage: "linear-gradient(to bottom, transparent 0%, black 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 100%)",
            }}
          />

          {/* Layer 2 */}
          <div
            className="absolute inset-0 backdrop-blur-[10px]"
            style={{
              maskImage: "linear-gradient(to bottom, transparent 35%, black 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 35%, black 100%)",
            }}
          />

          {/* Layer 3 */}
          <div
            className="absolute inset-0 backdrop-blur-[20px]"
            style={{
              maskImage: "linear-gradient(to bottom, transparent 65%, black 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 65%, black 100%)",
            }}
          />

          {/* Dark gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(to bottom, transparent 0%, rgba(2,2,2,0.8) 100%)",
            }}
          />

          {/* Content */}
          <div className="relative h-full px-4 flex items-end pb-3">
            <div className="flex items-end justify-between w-full">
              <div className="min-w-0">
                <p className="text-[17px] font-semibold text-text-primary leading-tight truncate">
                  {ritualist.name}
                </p>
                <p className="text-[13px] text-text-primary opacity-70 truncate">
                  @{ritualist.username}
                </p>
              </div>

              <p className="text-[13px] text-text-primary opacity-70 whitespace-nowrap ml-2">
                {"ritualCount" in ritualist
                  ? `${ritualist.ritualCount} ${t("connect.rituals")}`
                  : `${ritualist.followerCount} ${t("connect.followers")}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Expanded/hover state: full card overlay */}
      {isExpanded && (
        <div className="absolute inset-0 backdrop-blur-[10px] bg-[rgb(2,2,2,0.5)] flex flex-col justify-end p-5">
          {/* More icon top-right */}
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                onClick={e => e.stopPropagation()}
                className="absolute top-4 right-4 text-text-primary opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                aria-label={t("accessibility.moreOptions")}
              >
                <MoreVertical className="w-6 h-6" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/profile/${ritualist.id}`)}>
                {t("connect.viewProfile")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  const profileUrl = `${window.location.origin}/profile/${ritualist.id}`
                  navigator.clipboard.writeText(profileUrl)
                  toast.success(t("connect.linkCopied"))
                }}
              >
                {t("connect.copyLink")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowReportModal(true)}>
                {t("connect.report")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Name and handle */}
          <p className="text-[17px] font-semibold text-text-primary leading-tight">
            {ritualist.name}
          </p>
          <p className="text-[13px] text-text-primary opacity-70 mt-0.5 mb-1.5">
            @{ritualist.username}
          </p>

          {/* Bio */}
          {ritualist.bio && (
            <p className="text-[14px] leading-[25px] text-text-primary opacity-80 line-clamp-3 max-w-[260px] mt-2 mb-1">
              {ritualist.bio}
            </p>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              className="h-[50px] w-[125px] rounded-full text-[14px] font-semibold uppercase tracking-wide border border-text-primary text-text-primary cursor-pointer"
              onClick={() => navigate(`/profile/${ritualist.id}`)}
            >
              {t("connect.profile")}
            </button>
            {isGuest ? (
              <button
                type="button"
                className="h-[50px] w-[125px] rounded-full font-semibold uppercase tracking-wide flex items-center justify-center gap-2 transition-colors bg-connect-cream text-connect-cream-text hover:bg-connect-cream-hover text-[14px]"
                onClick={() => setShowLoginModal(true)}
              >
                <HeartOutlineIcon className="w-4 h-4" color="#000000" />
                {t("connect.follow")}
              </button>
            ) : (
              !ritualist.isFollowing && (
                <button
                  type="button"
                  className={`h-[50px] w-[125px] rounded-full font-semibold uppercase tracking-wide flex items-center justify-center gap-2 transition-colors ${
                    ritualist.isFollowing
                      ? "bg-brand-terracotta border text-[#F2F0EB] border-brand-terracotta text-[12px] hover:bg-brand-terracotta-hover disabled:opacity-20"
                      : "bg-connect-cream text-connect-cream-text hover:bg-connect-cream-hover text-[14px] disabled:opacity-20"
                  }`}
                  onClick={() => {
                    if (ritualist.isFollowing) {
                      onUnfollow?.(ritualist.id)
                    } else {
                      onFollow?.(ritualist.id)
                    }
                  }}
                >
                  {ritualist.isFollowing ? (
                    <HeartFilledIcon className="w-4 h-4" color="#000000" />
                  ) : (
                    <HeartOutlineIcon className="w-4 h-4" color="#000000" />
                  )}
                  {ritualist.isFollowing ? t("connect.unfollow") : t("connect.follow")}
                </button>
              )
            )}
          </div>
        </div>
      )}

      {showReportModal && (
        <ReportUserModal
          open={showReportModal}
          onOpenChange={setShowReportModal}
          user={{
            id: ritualist.id,
            name: ritualist.name,
            username: ritualist.username,
            profilePictureUrl: ritualist.profilePictureUrl,
          }}
        />
      )}

      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
